'use client';

import { useState, useEffect, useCallback, useMemo, useEffectEvent } from 'react';
import Link from '@/components/AppLink';
import PracticeImage from '@/components/PracticeImage';
import { AlertCircle, Timer, ChevronLeft, ChevronRight, Send, Check, X } from 'lucide-react';
import axiosInstance from '../../../utils/axiosInstance';
import { formatDisplayText } from '../../../utils/textUtils';

export interface QuestionItem {
  question: {
    _id: string;
    question: string;
    answer: string;
    question_image_url?: string;
  };
  options: {
    _id: string;
    option_text: string;
    option_type: string;
    path_url?: string;
  }[];
}

type Score = {
  correct: number;
  total: number;
  answered: number;
  percentage: number;
};

function normalizeTestHtml(html: string) {
  return html.replace(/&nbsp;/g, ' ').replace(/\u00a0/g, ' ');
}

function OnlineTest({ initialQuestions: questions, initialError: error = '', subTopicName }: { initialQuestions: QuestionItem[]; initialError?: string; subTopicName: string }) {
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [answers, setAnswers] = useState<Record<string, string>>({});
  const [timeLeft, setTimeLeft] = useState(30 * 60);
  const [isFinished, setIsFinished] = useState(false);
  const [score, setScore] = useState<Score | null>(null);

  const calculateScore = useCallback((questionsList: QuestionItem[], answersMap: Record<string, string>): Score => {
    let correct = 0;
    let answered = 0;
    questionsList.forEach(item => {
      const selectedOptionId = answersMap[item.question._id];
      if (selectedOptionId) {
        answered++;
        const selectedOption = item.options.find(opt => opt._id === selectedOptionId);
        if (selectedOption && selectedOption.option_text === item.question.answer) {
          correct++;
        }
      }
    });
    return {
      correct,
      total: questionsList.length,
      answered,
      percentage: questionsList.length ? Math.round((correct / questionsList.length) * 100) : 0
    };
  }, []);

  const saveTestResult = useCallback(async (topicName: string, testScore: Score) => {
    if (!topicName) return;
    try {
      await axiosInstance.post('/api/student/save-test-result', {
        subTopicName: topicName,
        score: testScore
      });
    } catch (err: unknown) {
      const responseStatus = typeof err === 'object' && err !== null && 'response' in err
        ? (err as { response?: { status?: number } }).response?.status
        : undefined;
      if (responseStatus !== 401) {
        console.error('Error saving test result:', err);
      }
    }
  }, []);

  const finishTest = useCallback((testScore: Score) => {
    setScore(testScore);
    setIsFinished(true);
    saveTestResult(subTopicName, testScore);
  }, [subTopicName, saveTestResult]);


  const expireTest = useEffectEvent(() => finishTest(calculateScore(questions, answers)));

  useEffect(() => {
    if (isFinished || questions.length === 0) return;
    const deadline = Date.now() + 30 * 60 * 1000;
    const timer = setInterval(() => {
      const remaining = Math.max(0, Math.ceil((deadline - Date.now()) / 1000));
      setTimeLeft(remaining);
      if (remaining === 0) {
        clearInterval(timer);
        expireTest();
      }
    }, 1000);
    return () => clearInterval(timer);
  }, [isFinished, questions.length]);

  const handleAnswer = useCallback((questionId: string, optionId: string) => {
    setAnswers(prev => ({ ...prev, [questionId]: optionId }));
  }, []);

  const formatTime = useCallback((seconds: number) => {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`;
  }, []);

  const submitTest = useCallback(() => {
    const testScore = calculateScore(questions, answers);
    finishTest(testScore);
  }, [questions, answers, calculateScore, finishTest]);

  const currentQuestion = useMemo(() => questions[currentQuestionIndex], [questions, currentQuestionIndex]);

  if (error) {
    return (
      <div className="study-theme min-h-screen flex items-center justify-center p-4">
        <div className="study-card text-center max-w-md mx-auto px-6 py-8">
          <div className="bg-red-500 bg-opacity-10 border border-red-500/30 rounded-2xl p-4 inline-block mb-4">
            <AlertCircle className="h-10 w-10 text-red-500" aria-hidden="true" />
          </div>
          <h1 className="text-2xl font-bold text-[var(--study-text)] mb-2">Something Went Wrong</h1>
          <p className="study-muted mb-6">{error}</p>
          <Link
            prefetch={false}
            href="/test-topics"
            className="study-button-primary inline-flex px-6 py-3 rounded-xl font-bold transition duration-300"
            aria-label="Go back to test topics"
          >
            Back to Topics
          </Link>
        </div>
      </div>
    );
  }

  if (isFinished && score) {
    return (
      <div className="study-theme min-h-screen p-4 pt-24">
        <div className="max-w-4xl mx-auto">
          <div className="study-card rounded-2xl p-8 mb-6 text-center">
            <div className="w-20 h-20 bg-[var(--study-primary)] rounded-full flex items-center justify-center mx-auto mb-6">
              <span className="text-white text-2xl font-bold">{score.percentage}%</span>
            </div>
            <h2 className="study-title text-3xl font-bold mb-2">Test Complete!</h2>
            <div className="study-muted mb-8 text-lg">
              You got {score.correct} out of {score.answered} answered questions correct
            </div>
            <div className="study-muted mb-8 text-sm">
              {score.total - score.answered} questions were skipped
            </div>
            <Link
              prefetch={false}
              href="/test-topics"
              className="study-button-primary inline-flex px-8 py-3 rounded-xl font-semibold text-lg transition-[color,background-color,border-color,opacity,transform] duration-200"
            >
              Back to Topics
            </Link>
          </div>

          <div className="study-card rounded-2xl p-4 sm:p-8">
            <h3 className="study-title text-2xl font-bold mb-6 text-center">Question Review</h3>
            <div className="space-y-4">
              {questions.map((item, index) => {
                const selectedOptionId = answers[item.question._id];
                const isAnswered = Boolean(selectedOptionId && selectedOptionId.trim());
                const selectedOption = isAnswered ? item.options.find(opt => opt._id === selectedOptionId) : null;
                const isCorrect = selectedOption ? selectedOption.option_text === item.question.answer : false;

                return (
                  <div key={item.question._id} className={`p-3 sm:p-4 rounded-xl border-2 ${!isAnswered
                    ? 'bg-yellow-500/10 border-yellow-500/30'
                    : isCorrect
                      ? 'bg-green-500/10 border-green-500/30'
                      : 'bg-red-500/10 border-red-500/30'
                    }`}>
                    <div className="flex flex-col sm:flex-row items-start gap-3">
                      <div className={`w-8 h-8 rounded-full flex items-center justify-center text-white font-bold text-sm flex-shrink-0 ${!isAnswered
                        ? 'bg-yellow-500'
                        : isCorrect
                          ? 'bg-green-500'
                          : 'bg-red-500'
                        }`}>
                        {index + 1}
                      </div>
                      <div className="flex-1 min-w-0 w-full">
                        <div className="flex flex-col sm:flex-row sm:items-start gap-2 mb-3">
                          <div
                            className="practice-content text-base sm:text-lg font-medium flex-1 min-w-0"
                            dangerouslySetInnerHTML={{ __html: normalizeTestHtml(item.question.question) }}
                          />
                          {!isAnswered && (
                            <span className="self-start text-yellow-700 font-semibold text-sm bg-yellow-500/15 px-3 py-1 rounded-full border border-yellow-500/30">
                              Skipped
                            </span>
                          )}
                        </div>

                        <div className="space-y-2">
                          {item.options.map((option, optIndex) => {
                            const isSelected = selectedOptionId === option._id;
                            const isCorrectOption = option.option_text === item.question.answer;

                            return (
                              <div key={option._id} className={`p-3 rounded-lg border ${isCorrectOption
                                ? 'bg-green-500/10 border-green-500/30 text-green-700 font-medium'
                                : isSelected
                                  ? 'bg-red-500/10 border-red-500/30 text-red-700'
                                  : 'bg-[var(--study-surface)] border-[var(--study-border)] text-[var(--study-muted)]'
                                }`}>
                                <div className="flex flex-wrap items-center gap-2">
                                  <span className={`w-5 h-5 rounded-full flex items-center justify-center text-xs font-bold ${isCorrectOption
                                    ? 'bg-green-500 text-white'
                                    : isSelected
                                      ? 'bg-red-500 text-white'
                                      : 'bg-[var(--study-border)] text-[var(--study-muted)]'
                                    }`}>
                                    {String.fromCharCode(65 + optIndex)}
                                  </span>
                                  <span
                                    className="practice-content flex-[1_1_8rem] min-w-0"
                                    dangerouslySetInnerHTML={{ __html: normalizeTestHtml(option.option_text) }}
                                  />
                                  {isCorrectOption && (
                                    <span className="text-green-700 font-semibold text-sm flex items-center gap-1 flex-shrink-0">
                                      <Check className="w-4 h-4" /> Correct
                                    </span>
                                  )}
                                  {isSelected && !isCorrectOption && (
                                    <span className="text-red-700 font-semibold text-sm flex items-center gap-1 flex-shrink-0">
                                      <X className="w-4 h-4" /> Your Answer
                                    </span>
                                  )}
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (questions.length === 0) {
    return (
      <div className="study-theme min-h-screen flex items-center justify-center p-4">
        <div className="study-card text-center max-w-md mx-auto px-6 py-8">
          <div className="study-icon rounded-full p-4 inline-flex mb-4">
            <AlertCircle className="h-10 w-10" aria-hidden="true" />
          </div>
          <h2 className="text-2xl font-bold text-[var(--study-text)] mb-2">No Questions Available</h2>
          <p className="study-muted mb-6">Questions will appear here once they are added for this subtopic.</p>
          <Link
            prefetch={false}
            href="/test-topics"
            className="study-button-primary inline-flex px-6 py-2 rounded-lg font-semibold transition-[color,background-color,border-color,opacity,transform]"
          >
            Back to Topics
          </Link>
        </div>
      </div>
    );
  }

  if (!currentQuestion) return null;

  return (
    <div className="study-theme min-h-screen flex items-center justify-center p-2 sm:p-4 pt-24">
      <div className="w-full max-w-4xl">
        <div className="practice-question-card rounded-2xl flex flex-col max-h-[85vh] sm:max-h-[85vh] overflow-hidden">
          <div className="practice-question-header p-3 sm:p-4 flex-shrink-0">
            <h1 className="text-lg sm:text-xl font-bold text-[var(--study-text)] mb-2 text-center">Online Test - {formatDisplayText(subTopicName)}</h1>
            <div className="flex justify-between items-center text-[var(--study-text)]">
              <div className="flex items-center space-x-3">
                <div className="w-8 h-8 bg-[var(--study-primary)] rounded-full flex items-center justify-center">
                  <span className="text-white font-bold text-sm">{currentQuestionIndex + 1}</span>
                </div>
                <div>
                  <h2 className="text-sm font-medium">Question {currentQuestionIndex + 1} of {questions.length}</h2>
                </div>
              </div>
              <div className="flex items-center space-x-2 bg-[var(--study-surface)] border border-[var(--study-border)] px-2 sm:px-3 py-1 rounded-lg">
                <Timer className="h-3 w-3 sm:h-4 sm:w-4 text-[var(--study-primary)]" />
                <span className="font-mono text-xs sm:text-sm text-[var(--study-text)]">{formatTime(timeLeft)}</span>
              </div>
            </div>
            <div className="mt-2 bg-[var(--study-border)] rounded-full h-1.5">
              <div
                className="bg-[var(--study-primary)] rounded-full h-1.5 transition-[color,background-color,border-color,opacity,transform] duration-500"
                style={{ width: `${((currentQuestionIndex + 1) / questions.length) * 100}%` }}
              ></div>
            </div>
          </div>

          <div className="flex-1 p-3 sm:p-4 overflow-y-auto">
            <div
              className="practice-content practice-question-body text-lg sm:text-xl font-medium mb-4 sm:mb-6 p-4 sm:p-6 rounded-xl text-[1.125rem]"
              dangerouslySetInnerHTML={{ __html: normalizeTestHtml(currentQuestion.question.question) }}
            />
            {currentQuestion.question.question_image_url && (
              <div className="mb-3 sm:mb-4">
                <PracticeImage src={`${process.env.NEXT_PUBLIC_API_URL}/uploads/${currentQuestion.question.question_image_url}`} alt="Question" />
              </div>
            )}
            <div className="space-y-2">
              {currentQuestion.options.map((option, index) => (
                <button
                  key={option._id}
                  type="button"
                  aria-pressed={answers[currentQuestion.question._id] === option._id}
                  onClick={() => handleAnswer(currentQuestion.question._id, option._id)}
                  className={`group w-full p-3 text-left rounded-xl border-2 transition-[color,background-color,border-color,opacity,transform] duration-200 cursor-pointer ${answers[currentQuestion.question._id] === option._id
                    ? 'border-[var(--study-primary)] bg-[var(--study-primary-soft)]'
                    : 'practice-option-idle'
                    }`}
                >
                  <div className="flex items-center space-x-3">
                    <div className={`w-6 h-6 sm:w-7 sm:h-7 rounded-full flex items-center justify-center text-xs sm:text-sm font-bold transition-[color,background-color,border-color,opacity,transform] flex-shrink-0 ${answers[currentQuestion.question._id] === option._id
                      ? 'bg-[var(--study-primary)] text-white'
                      : 'bg-[var(--study-border)] text-[var(--study-muted)] group-hover:bg-[var(--study-primary-soft)]'
                      }`}>
                      {String.fromCharCode(65 + index)}
                    </div>
                    <div className="flex-1 min-w-0">
                      {option.option_type === 'text' ? (
                        <span
                          className="practice-content text-sm sm:text-base block font-normal"
                          dangerouslySetInnerHTML={{ __html: normalizeTestHtml(option.option_text) }}
                        />
                      ) : (
                        <>
                          {option.option_text && <span className="text-[var(--study-muted)] text-sm sm:text-base block mb-2 font-normal">{option.option_text}</span>}
                          {option.path_url && (
                            <PracticeImage src={`${process.env.NEXT_PUBLIC_API_URL}/uploads/${option.path_url}`} alt="Option" compact />
                          )}
                        </>
                      )}
                    </div>
                  </div>
                </button>
              ))}
            </div>
          </div>

          <div className="bg-[var(--study-surface-soft)] border-t border-[var(--study-border)] px-3 sm:px-4 py-3 flex justify-between items-center flex-shrink-0">
            <button
              onClick={() => setCurrentQuestionIndex(prev => Math.max(0, prev - 1))}
              disabled={currentQuestionIndex === 0}
              className="flex items-center space-x-1 sm:space-x-2 px-3 sm:px-4 py-2 text-[var(--study-muted)] disabled:opacity-45 disabled:cursor-not-allowed hover:text-[var(--study-primary)] transition-colors font-medium text-sm sm:text-base cursor-pointer"
            >
              <ChevronLeft className="h-4 w-4" />
              <span>Previous</span>
            </button>
            {currentQuestionIndex === questions.length - 1 ? (
              <button
                onClick={submitTest}
                className="study-button-primary flex items-center space-x-1 sm:space-x-2 px-4 sm:px-6 py-2 rounded-lg font-medium transition-[color,background-color,border-color,opacity,transform] duration-200 text-sm sm:text-base cursor-pointer"
              >
                <Send className="h-4 w-4" />
                <span>Submit Test</span>
              </button>
            ) : (
              <button
                onClick={() => setCurrentQuestionIndex(prev => Math.min(questions.length - 1, prev + 1))}
                className="flex items-center space-x-1 sm:space-x-2 px-3 sm:px-4 py-2 text-[var(--study-muted)] hover:text-[var(--study-primary)] transition-colors font-medium text-sm sm:text-base cursor-pointer"
              >
                <span>Next</span>
                <ChevronRight className="h-4 w-4" />
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default OnlineTest;
